import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useSWR, { mutate } from "swr";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { formatDate, todayISO } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, ClipboardCheck, Trash2, Eye, ChevronLeft, Printer } from "lucide-react";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";
import PhotoUploader from "@/components/PhotoUploader";

const fetcher = (url) => api.get(url).then((r) => r.data);

const CONDITION_OPTIONS = [
  { v: "baik", label: "Baik" },
  { v: "kurang", label: "Kurang" },
  { v: "rusak", label: "Rusak" },
  { v: "tidak_ada", label: "Tidak Ada" },
];

const COND_TONE = {
  baik: "bg-success/15 text-success",
  kurang: "bg-warning/15 text-warning",
  rusak: "bg-destructive/15 text-destructive",
  tidak_ada: "bg-muted text-muted-foreground",
};

const initialForm = {
  unit_id: "", tenant_id: "", contract_id: "",
  type: "checkin", date: todayISO(),
  water_meter: "", electricity_meter: "",
  checklist: [],
  damage_notes: "",
  photos: [], // {id, ...}
  signature_data_url: "",
  signed_by_name: "",
  handled_by_name: "",
  notes: "",
};

export default function Handovers() {
  const { data: handovers = [], isLoading } = useSWR("/handovers", fetcher);
  const { data: units = [] } = useSWR("/units", fetcher);
  const { data: tenants = [] } = useSWR("/tenants", fetcher);
  const { data: contracts = [] } = useSWR("/contracts", fetcher);
  const unitMap = useMemo(() => Object.fromEntries(units.map((u) => [u.id, u])), [units]);
  const tenantMap = useMemo(() => Object.fromEntries(tenants.map((t) => [t.id, t])), [tenants]);

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [detail, setDetail] = useState(null);
  const navigate = useNavigate();

  const openNew = async () => {
    setForm(initialForm);
    setOpen(true);
    // load default checklist template
    try {
      const { data } = await api.get("/handovers/checklist-template");
      const withUid = (data.items || []).map((it) => ({ ...it, _uid: crypto.randomUUID() }));
      setForm((f) => ({ ...f, checklist: withUid }));
    } catch (e) {
      console.warn("Gagal load checklist template:", e);
    }
  };

  const onSelectUnit = (unit_id) => {
    const active = contracts.find((c) => c.unit_id === unit_id && c.status === "active");
    setForm({
      ...form,
      unit_id,
      tenant_id: active?.tenant_id || form.tenant_id,
      contract_id: active?.id || "",
    });
  };

  const setChecklistItem = (i, patch) => {
    const next = form.checklist.slice();
    next[i] = { ...next[i], ...patch };
    setForm({ ...form, checklist: next });
  };

  const addChecklistItem = () => {
    setForm({ ...form, checklist: [...form.checklist, { _uid: crypto.randomUUID(), label: "Item baru", condition: "baik", notes: "" }] });
  };
  const removeChecklistItem = (i) => {
    setForm({ ...form, checklist: form.checklist.filter((_, idx) => idx !== i) });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.unit_id) { toast.error("Pilih unit dulu."); return; }
    if (!form.signature_data_url) { toast.error("Tanda tangan wajib diisi."); return; }
    setSaving(true);
    try {
      const body = {
        unit_id: form.unit_id,
        tenant_id: form.tenant_id || null,
        contract_id: form.contract_id || null,
        type: form.type,
        date: form.date,
        water_meter: form.water_meter === "" ? null : Number(form.water_meter),
        electricity_meter: form.electricity_meter === "" ? null : Number(form.electricity_meter),
        checklist: form.checklist,
        damage_notes: form.damage_notes,
        photo_ids: form.photos.map((p) => p.id),
        signature_data_url: form.signature_data_url,
        signed_by_name: form.signed_by_name,
        handled_by_name: form.handled_by_name,
        notes: form.notes,
      };
      const { data } = await api.post("/handovers", body);
      toast.success("Berita acara serah terima disimpan");
      setOpen(false);
      mutate("/handovers");
      setDetail(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (h) => {
    if (!confirm("Hapus berita acara?")) return;
    try {
      await api.delete(`/handovers/${h.id}`);
      toast.success("Berita acara dihapus");
      mutate("/handovers");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="handovers-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Operasional Lapangan</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Serah Terima Unit</h1>
          <p className="text-sm text-muted-foreground mt-1">Berita acara check-in / check-out — kondisi, meteran, foto, dan tanda tangan.</p>
        </div>
        <Button onClick={openNew} className="gap-2" data-testid="add-handover-button">
          <Plus className="h-4 w-4" /> Buat Berita Acara
        </Button>
      </header>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Memuat...</div>
      ) : handovers.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 flex flex-col items-center text-center gap-4">
            <div className="h-14 w-14 rounded-md bg-primary/10 text-primary grid place-items-center">
              <ClipboardCheck className="h-7 w-7" />
            </div>
            <div className="font-display text-lg font-medium">Belum ada berita acara</div>
            <p className="text-sm text-muted-foreground -mt-2">Catat kondisi unit saat penyewa masuk atau keluar.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {handovers.map((h) => (
            <Card key={h.id} data-testid={`handover-card-${h.id}`}>
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge className={`rounded-full text-[10px] uppercase tracking-wider ${h.type === "checkin" ? "bg-info/15 text-info" : "bg-warning/15 text-warning"}`}>
                        {h.type === "checkin" ? "Check-in" : "Check-out"}
                      </Badge>
                      <Badge className="rounded-md font-mono">{unitMap[h.unit_id]?.code || "?"}</Badge>
                    </div>
                    <div className="mt-2 font-medium truncate">{tenantMap[h.tenant_id]?.name || "—"}</div>
                    <div className="text-xs text-muted-foreground">{formatDate(h.date)} · {h.photo_ids?.length || 0} foto</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => setDetail(h)} data-testid={`view-handover-${h.id}`}>
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => del(h)} data-testid={`delete-handover-${h.id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Berita Acara Serah Terima</DialogTitle>
            <DialogDescription>Catat kondisi unit, meteran, foto, dan tanda tangan digital.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="space-y-1.5 col-span-2">
                <Label>Unit</Label>
                <Select value={form.unit_id} onValueChange={onSelectUnit}>
                  <SelectTrigger data-testid="handover-unit-select"><SelectValue placeholder="Pilih unit" /></SelectTrigger>
                  <SelectContent>
                    {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.code} — {u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label>Penyewa</Label>
                <Select value={form.tenant_id || "none"} onValueChange={(v) => setForm({ ...form, tenant_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="handover-tenant-select"><SelectValue placeholder="Pilih penyewa" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Tidak ada —</SelectItem>
                    {tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Jenis</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="handover-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="checkin">Check-in (Masuk)</SelectItem>
                    <SelectItem value="checkout">Check-out (Keluar)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Tanggal</Label>
                <Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="handover-date-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Meter Air</Label>
                <Input type="number" step="0.01" value={form.water_meter} onChange={(e) => setForm({ ...form, water_meter: e.target.value })} placeholder="m³" data-testid="handover-water-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Meter Listrik</Label>
                <Input type="number" step="0.01" value={form.electricity_meter} onChange={(e) => setForm({ ...form, electricity_meter: e.target.value })} placeholder="kWh" data-testid="handover-elec-input" />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Checklist Kondisi</Label>
                <Button type="button" size="sm" variant="ghost" onClick={addChecklistItem} className="gap-1 h-7">
                  <Plus className="h-3.5 w-3.5" /> Tambah item
                </Button>
              </div>
              <div className="rounded-md border border-border/60 divide-y divide-border/50">
                {form.checklist.map((item, i) => (
                  <div key={item._uid || `chk-${i}`} className="p-3 grid grid-cols-1 sm:grid-cols-12 gap-2 items-start">
                    <Input className="sm:col-span-4 h-9" value={item.label} onChange={(e) => setChecklistItem(i, { label: e.target.value })} />
                    <Select value={item.condition} onValueChange={(v) => setChecklistItem(i, { condition: v })}>
                      <SelectTrigger className="sm:col-span-3 h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CONDITION_OPTIONS.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input className="sm:col-span-4 h-9" placeholder="Catatan (opsional)" value={item.notes} onChange={(e) => setChecklistItem(i, { notes: e.target.value })} />
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeChecklistItem(i)} className="sm:col-span-1 h-9 w-9 justify-self-end">
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
                {form.checklist.length === 0 && (
                  <div className="p-4 text-sm text-muted-foreground">Belum ada item. Klik "Tambah item".</div>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Catatan Kerusakan</Label>
              <Textarea rows={2} value={form.damage_notes} onChange={(e) => setForm({ ...form, damage_notes: e.target.value })} data-testid="handover-damage-input" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Foto Kondisi</Label>
              <PhotoUploader value={form.photos} onChange={(v) => setForm({ ...form, photos: v })} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Nama Penyewa (untuk TTD)</Label>
                  <Input value={form.signed_by_name} onChange={(e) => setForm({ ...form, signed_by_name: e.target.value })} data-testid="handover-signed-by-input" />
                </div>
                <SignaturePad
                  value={form.signature_data_url}
                  onChange={(v) => setForm({ ...form, signature_data_url: v })}
                  label="Tanda tangan penyewa"
                  testid="handover-signature-pad"
                />
              </div>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Petugas / Admin</Label>
                  <Input value={form.handled_by_name} onChange={(e) => setForm({ ...form, handled_by_name: e.target.value })} data-testid="handover-handled-by-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Catatan Umum</Label>
                  <Textarea rows={5} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button type="submit" disabled={saving} data-testid="handover-save-button">
                {saving ? "Menyimpan..." : "Simpan Berita Acara"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle>Berita Acara — {detail.type === "checkin" ? "Check-in" : "Check-out"}</DialogTitle>
                <DialogDescription>{formatDate(detail.date)} · {unitMap[detail.unit_id]?.code}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <Info label="Unit" value={`${unitMap[detail.unit_id]?.code || ""} — ${unitMap[detail.unit_id]?.name || ""}`} />
                  <Info label="Penyewa" value={tenantMap[detail.tenant_id]?.name} />
                  <Info label="Meter Air" value={detail.water_meter != null ? `${detail.water_meter} m³` : "—"} />
                  <Info label="Meter Listrik" value={detail.electricity_meter != null ? `${detail.electricity_meter} kWh` : "—"} />
                </div>
                {detail.checklist?.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2">Checklist</div>
                    <div className="rounded-md border border-border/60 divide-y divide-border/50">
                      {detail.checklist.map((c, i) => (
                        <div key={i} className="p-2.5 flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-medium truncate">{c.label}</div>
                            {c.notes && <div className="text-xs text-muted-foreground truncate">{c.notes}</div>}
                          </div>
                          <Badge className={`rounded-full text-[10px] uppercase tracking-wider ${COND_TONE[c.condition]}`}>
                            {CONDITION_OPTIONS.find((o) => o.v === c.condition)?.label}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {detail.damage_notes && <Info label="Catatan Kerusakan" value={detail.damage_notes} />}
                {detail.photo_ids?.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2">Foto</div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {detail.photo_ids.map((pid) => (
                        <div key={pid} className="aspect-square rounded-md overflow-hidden border border-border/60">
                          <img src={`${API}/uploads/${pid}`} alt="foto" className="h-full w-full object-cover" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {detail.signature_data_url && (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2">Tanda Tangan</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-md border border-border/60 bg-white p-2">
                        <img src={detail.signature_data_url} alt="ttd" className="h-24 mx-auto object-contain" />
                        <div className="text-center text-xs mt-1 font-medium">{detail.signed_by_name || "Penyewa"}</div>
                      </div>
                      <div className="rounded-md border border-border/60 p-3">
                        <div className="text-xs text-muted-foreground">Petugas</div>
                        <div className="font-medium">{detail.handled_by_name || "—"}</div>
                      </div>
                    </div>
                  </div>
                )}
                {detail.notes && <Info label="Catatan" value={detail.notes} />}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDetail(null)}>Tutup</Button>
                <Button onClick={() => window.print()} className="gap-1.5">
                  <Printer className="h-4 w-4" /> Cetak
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
      <div className="font-medium">{value || "—"}</div>
    </div>
  );
}
